import type {
  BrowseCategoriesParams,
  BrowseProductsParams,
  CategoryListRequest,
  MediaOwnerType,
  MediaVisibility,
  ProductListRequest,
  ProductSearchRequest,
} from '../model/types';
import type { ListPricingRulesParams, ResolvePriceParams } from '../api/catalogPricingApi';
import type { ListPriceChangeBatchesParams } from '../model/priceBatchTypes';
import type { ListMarkdownPlansParams } from '../model/markdownPlanTypes';

/** Centralised query keys; everything sits under the `catalog` root so a tenant switch can drop it all. */
export const CATALOG_ROOT_KEY = 'catalog';

/**
 * OKEI unit-of-measure dictionary (plan-catalog-008) — deliberately NOT nested under
 * `CATALOG_ROOT_KEY`: it's global, non-tenant-scoped reference data (unlike everything else in
 * `catalogKeys`), so a tenant switch shouldn't drop/refetch it, and its fixed shape (`['okei-units']`)
 * is part of the BFF contract this feature was built against.
 */
export const OKEI_UNITS_KEY = ['okei-units'] as const;

export const catalogKeys = {
  all: [CATALOG_ROOT_KEY] as const,

  products: [CATALOG_ROOT_KEY, 'products'] as const,
  productsList: (req: ProductListRequest) => [CATALOG_ROOT_KEY, 'products', 'list', req] as const,
  productsSearch: (req: ProductSearchRequest) =>
    [CATALOG_ROOT_KEY, 'products', 'search', req] as const,
  productsBrowse: (params: BrowseProductsParams) =>
    [CATALOG_ROOT_KEY, 'products', 'browse', params] as const,
  product: (id: string) => [CATALOG_ROOT_KEY, 'products', 'detail', id] as const,
  productPublication: (id: string) =>
    [CATALOG_ROOT_KEY, 'products', 'publication', id] as const,

  // ── Product PIM v2: variants + version history (epic-catalog-004/005) ──────────────────────
  productVariants: (productId: string, locale?: string) =>
    [CATALOG_ROOT_KEY, 'products', 'variants', productId, locale ?? null] as const,
  productVersions: (productId: string, params: { page?: number; size?: number }) =>
    [CATALOG_ROOT_KEY, 'products', 'versions', productId, params] as const,
  productVersion: (productId: string, number: number) =>
    [CATALOG_ROOT_KEY, 'products', 'versions', 'detail', productId, number] as const,
  productBarcodes: (productId: string) =>
    [CATALOG_ROOT_KEY, 'products', 'barcodes', productId] as const,
  /** Every stored translation of a product (adr-tenant-005) — feeds the card-language switcher. */
  productTranslations: (productId: string) =>
    [CATALOG_ROOT_KEY, 'products', 'translations', productId] as const,

  categories: [CATALOG_ROOT_KEY, 'categories'] as const,
  categoriesList: (req: CategoryListRequest) =>
    [CATALOG_ROOT_KEY, 'categories', 'list', req] as const,
  categoriesSearch: (params: BrowseCategoriesParams) =>
    [CATALOG_ROOT_KEY, 'categories', 'search', params] as const,
  category: (id: string) => [CATALOG_ROOT_KEY, 'categories', 'detail', id] as const,
  /** Full tenant tree (epic-catalog-005, user-story-catalog-011) — `locale` scopes the cache entry. */
  categoryTree: (locale?: string) => [CATALOG_ROOT_KEY, 'categories', 'tree', locale ?? null] as const,
  categoryVersions: (categoryId: string, params: { page?: number; size?: number }) =>
    [CATALOG_ROOT_KEY, 'categories', 'versions', categoryId, params] as const,
  categoryVersion: (categoryId: string, number: number) =>
    [CATALOG_ROOT_KEY, 'categories', 'versions', 'detail', categoryId, number] as const,
  /** Every stored translation of a category (adr-tenant-005) — feeds the card-language switcher. */
  categoryTranslations: (categoryId: string) =>
    [CATALOG_ROOT_KEY, 'categories', 'translations', categoryId] as const,
  mediaAssets: (params: { ownerType: MediaOwnerType; ownerId?: string; visibility?: MediaVisibility }) =>
    [CATALOG_ROOT_KEY, 'media', 'assets', params] as const,

  pricing: [CATALOG_ROOT_KEY, 'pricing'] as const,
  pricingConfig: [CATALOG_ROOT_KEY, 'pricing', 'config'] as const,
  pricingRules: (params: ListPricingRulesParams) =>
    [CATALOG_ROOT_KEY, 'pricing', 'rules', params] as const,
  providerConfigs: [CATALOG_ROOT_KEY, 'pricing', 'providers'] as const,
  providerCredential: (configId: string) =>
    [CATALOG_ROOT_KEY, 'pricing', 'providers', configId, 'credential'] as const,
  approvals: (params: { page?: number; size?: number }) =>
    [CATALOG_ROOT_KEY, 'pricing', 'approvals', params] as const,
  protectionPolicy: [CATALOG_ROOT_KEY, 'pricing', 'protection-policy'] as const,
  audit: (params: { productId?: string; page?: number; size?: number }) =>
    [CATALOG_ROOT_KEY, 'pricing', 'audit', params] as const,
  pricingAuditLog: (params: { customerId?: string; page?: number; size?: number }) =>
    [CATALOG_ROOT_KEY, 'pricing', 'calculation-audit', params] as const,

  salesChannels: [CATALOG_ROOT_KEY, 'pricing', 'sales-channels'] as const,
  priceLines: (productId: string) =>
    [CATALOG_ROOT_KEY, 'pricing', 'price-lines', productId] as const,
  priceResolution: (params: ResolvePriceParams) =>
    [CATALOG_ROOT_KEY, 'pricing', 'resolution', params] as const,

  // ── Price lists (adr-pricing-025, plan-platform-002 WP4.2) ──────────────────
  // `priceList`/`priceListBindings`/`priceListLines` nest UNDER `priceLists` on purpose (unlike
  // the client-only-cache anti-pattern documented in feedback_tanstack-invalidatequeries-prefix-collision):
  // these are genuine server-fetched children of one list, so invalidating the whole `priceLists`
  // branch (e.g. on delete) correctly sweeps its bindings/lines too.
  priceLists: [CATALOG_ROOT_KEY, 'pricing', 'price-lists'] as const,
  priceList: (code: string) => [CATALOG_ROOT_KEY, 'pricing', 'price-lists', 'detail', code] as const,
  priceListBindings: (code: string) =>
    [CATALOG_ROOT_KEY, 'pricing', 'price-lists', 'detail', code, 'bindings'] as const,
  priceListLines: (code: string, params: { page?: number; size?: number } = {}) =>
    [CATALOG_ROOT_KEY, 'pricing', 'price-lists', 'detail', code, 'lines', params] as const,
  retailStores: [CATALOG_ROOT_KEY, 'pricing', 'retail-stores'] as const,
  productPrice: (productId: string) => [CATALOG_ROOT_KEY, 'pricing', 'product-price', productId] as const,
  pricingQuote: (quoteId: string) => [CATALOG_ROOT_KEY, 'pricing', 'quote', quoteId] as const,
  pricingSnapshot: (orderId: string) => [CATALOG_ROOT_KEY, 'pricing', 'snapshot', orderId] as const,
  discountRoundingPolicy: [CATALOG_ROOT_KEY, 'pricing', 'discount-rounding-policy'] as const,
  roundingPolicies: [CATALOG_ROOT_KEY, 'pricing', 'rounding-policies'] as const,

  // ── Customer groups (epic-pricing-002 Phase 1, plan-platform-002 WP4.3) ─────
  customerGroups: [CATALOG_ROOT_KEY, 'pricing', 'customer-groups'] as const,
  customerGroupMembers: (code: string) =>
    [CATALOG_ROOT_KEY, 'pricing', 'customer-groups', code, 'members'] as const,

  // ── Bulk repricing batches (plan-platform-002 WP4.3) ────────────────────────
  priceChangeBatches: (params: ListPriceChangeBatchesParams) =>
    [CATALOG_ROOT_KEY, 'pricing', 'batches', params] as const,
  priceChangeBatch: (batchId: string) =>
    [CATALOG_ROOT_KEY, 'pricing', 'batches', 'detail', batchId] as const,

  // ── Markdown plans (plan-platform-002 WP4.3) ────────────────────────────────
  markdownPlans: (params: ListMarkdownPlansParams) =>
    [CATALOG_ROOT_KEY, 'pricing', 'markdown-plans', params] as const,
  markdownPlan: (planId: string) =>
    [CATALOG_ROOT_KEY, 'pricing', 'markdown-plans', 'detail', planId] as const,

  googleSheet: [CATALOG_ROOT_KEY, 'import', 'google-sheet'] as const,
  importRun: (id: string) => [CATALOG_ROOT_KEY, 'import', 'runs', id] as const,
  catalogFeedFile: (fileId: string) => [CATALOG_ROOT_KEY, 'import', 'feed', 'files', fileId] as const,
  catalogFeedColumnMapping: [CATALOG_ROOT_KEY, 'import', 'feed', 'column-mapping'] as const,

  // Product-360 stock panel (ProductEditPage) — read-only, scoped to one product.
  productStock: (productId: string) => [CATALOG_ROOT_KEY, 'product-stock', productId] as const,
  productStockLocations: [CATALOG_ROOT_KEY, 'product-stock', 'locations'] as const,

  // ── PIM structure (epic-catalog-004/005) ────────────────────────────────────
  languages: [CATALOG_ROOT_KEY, 'languages'] as const,

  attributes: [CATALOG_ROOT_KEY, 'attributes'] as const,
  attribute: (code: string) => [CATALOG_ROOT_KEY, 'attributes', 'detail', code] as const,

  attributeGroups: [CATALOG_ROOT_KEY, 'attribute-groups'] as const,
  attributeGroup: (code: string) => [CATALOG_ROOT_KEY, 'attribute-groups', 'detail', code] as const,

  productFamilies: [CATALOG_ROOT_KEY, 'product-families'] as const,
  productFamily: (code: string) => [CATALOG_ROOT_KEY, 'product-families', 'detail', code] as const,

  measurementFamilies: [CATALOG_ROOT_KEY, 'measurement-families'] as const,
  measurementFamily: (code: string) =>
    [CATALOG_ROOT_KEY, 'measurement-families', 'detail', code] as const,

  // ── Publication workspace (epic-catalog-001) ────────────────────────────────
  publicationCatalogs: [CATALOG_ROOT_KEY, 'publication', 'catalogs'] as const,
  publicationCatalog: (catalogId: string) =>
    [CATALOG_ROOT_KEY, 'publication', 'catalogs', 'detail', catalogId] as const,
  publicationPreview: (catalogId: string) =>
    [CATALOG_ROOT_KEY, 'publication', 'catalogs', catalogId, 'preview'] as const,
  /** Prefix (no `params` tail) — invalidate this to refetch every page of a catalog's runs at once. */
  publicationRunsRoot: (catalogId: string) =>
    [CATALOG_ROOT_KEY, 'publication', 'catalogs', catalogId, 'runs'] as const,
  publicationRuns: (catalogId: string, params: { page?: number; size?: number }) =>
    [CATALOG_ROOT_KEY, 'publication', 'catalogs', catalogId, 'runs', params] as const,
  publicationRun: (catalogId: string, runId: string) =>
    [CATALOG_ROOT_KEY, 'publication', 'catalogs', catalogId, 'runs', 'detail', runId] as const,
  publicationRules: (catalogId: string) =>
    [CATALOG_ROOT_KEY, 'publication', 'catalogs', catalogId, 'rules'] as const,
  publicationSalesChannels: [CATALOG_ROOT_KEY, 'publication', 'sales-channels'] as const,
  publicationChannelBindings: (channelId: string) =>
    [CATALOG_ROOT_KEY, 'publication', 'sales-channels', channelId, 'catalog-bindings'] as const,
  publicationCatalogTypes: [CATALOG_ROOT_KEY, 'publication', 'catalog-types'] as const,
  publicationProfile: (catalogTypeCode: string) =>
    [CATALOG_ROOT_KEY, 'publication', 'catalog-types', catalogTypeCode, 'profile'] as const,
} as const;
